import React from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/lib/AuthContext'
import { useCommunity } from '@/lib/CommunityContext'
import { useRoute } from '@/lib/RouteContext'
import { type Section } from '@/types/sections'
import { clearMenuCounters, markSpaceSeen, refreshMenuCounters } from '@/lib/menuCounters'

/** Long enough for the space's own `GET` (which records the visit) to land first. */
const AFTER_NAVIGATION_MS = 2500
const TICK_MS = 2 * 60 * 1000

/**
 * F-191 phase 2b: keeps the menu's "news since your last visit" fresh. Fetches
 * on sign-in, after each navigation, when the tab comes back and on a slow
 * tick; the space being opened is zeroed at once. Renders nothing.
 */
export const MenuCountersSync = ({ sections }: { sections: Section[] }) => {
  const { isAuthenticated } = useAuth()
  const { community } = useCommunity()
  const { currentPath } = useRoute()
  const communityId = community?.id ?? null
  const enabled = isAuthenticated && Boolean(communityId)

  React.useEffect(() => {
    clearMenuCounters()
    if (!enabled) return
    void refreshMenuCounters()
    const tick = window.setInterval(() => { if (document.visibilityState === 'visible') void refreshMenuCounters() }, TICK_MS)
    const onVisible = () => { if (document.visibilityState === 'visible') void refreshMenuCounters() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(tick)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [enabled, communityId])

  /* F-252: the counters follow the menu's language (a space of another language does not count). */
  const { i18n } = useTranslation()
  const language = i18n.language
  const languageRef = React.useRef(language)
  React.useEffect(() => {
    if (languageRef.current === language) return
    languageRef.current = language
    if (enabled) void refreshMenuCounters()
  }, [enabled, language])

  /* `/<section>/<space>/…`: the space on screen has no news left to show. */
  const [sectionSlug, spaceSlug] = currentPath.split(/[?#]/)[0].split('/').filter(Boolean)
  const space = React.useMemo(() => {
    const section = sections.find((item) => item.slug === sectionSlug)
    const found = section?.spaces?.find((item) => item.slug === spaceSlug)
    return section && found ? { spaceId: found.id, sectionId: section.id } : null
  }, [sections, sectionSlug, spaceSlug])

  React.useEffect(() => {
    if (!enabled) return
    if (space) markSpaceSeen(space.spaceId, space.sectionId)
    const timer = window.setTimeout(() => { void refreshMenuCounters() }, AFTER_NAVIGATION_MS)
    return () => window.clearTimeout(timer)
  }, [enabled, currentPath, space])

  return null
}
